import { Badge } from '@/components/ui/badge';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { PlatformBadge } from '@/components/shared/platform/PlatformBadge';
import {
  dedupeByPlatformLabel,
  MULTI_TOOL_TOOLTIP,
} from '@/components/shared/platform/platform-utils';
import { MULTI_TOOL_PLATFORM_ID } from '@kirin/api-client/contracts/platform-registry';
import type {
  GroupedAlert,
  AlertOccurrence,
  Outcome,
} from '@kirin/api-client/contracts/alerts';
import { getAlertFileDisplayLabel } from '@kirin/api-client/contracts';
import type { ToolEventKind } from '@kirin/api-client/contracts';
import {
  formatAlertType,
  formatSubPolicyLabel,
} from '@kirin/api-client/helpers/alerts';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { ShieldCheck, Eye, HelpCircle, Sparkles } from 'lucide-react';

export function FilePathCellValue({
  filePath,
  location,
  eventKind,
  className,
}: {
  filePath?: string | null;
  /** The alert/occurrence `location` — the real file path when one is known. */
  location?: string | null;
  /** KIR-3765: first-class tool-event side, drives the Tool Input/Output label. */
  eventKind?: ToolEventKind | null;
  className?: string;
}) {
  if (!filePath && !location) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  const label = getAlertFileDisplayLabel({ filePath, location, eventKind });
  // KIR-2563: width-measured middle truncation keeps the filename visible and
  // the full path copyable via a dedicated affordance. Only that affordance
  // stops propagation, so clicking the cell still triggers the row action.
  // KIR-3810: alert File cells also get a one-click copy button.
  return (
    <MiddleTruncatedPath path={label} className={className} showInlineCopy />
  );
}

// KIR-2941: shared column width for the Title column rendered by TypeCell.
// KIR-5057: 220px is the design width. TypeCell clamps titles to two lines and
// puts the full text in a tooltip, so the longest registry titles (50+ chars)
// are expected to ellipsize. The org and personal alerts tables and the grouped
// AlertsTable all import this so they stay in lockstep.
export const TITLE_COLUMN_WIDTH = 220;

export function TypeCell({
  row,
  revampEnabled = false,
}: {
  row: GroupedAlert;
  // KIR-2381: threaded from the column factory so the flag is read once,
  // not per row. Default `false` keeps the legacy off-state byte-identical.
  revampEnabled?: boolean;
}) {
  const label = revampEnabled
    ? formatSubPolicyLabel(row.subPolicyKey, row.type)
    : formatAlertType(row.type);
  // Plain text, wrapped and clamped to two lines (the cell is nowrap by
  // default); the native tooltip carries anything the clamp cuts off.
  return (
    <span
      className="line-clamp-2 whitespace-normal break-words text-sm font-medium"
      title={label}
    >
      {label}
    </span>
  );
}

export function SeverityCell({ row }: { row: GroupedAlert }) {
  return <SeverityBadge riskLevel={row.severity} showIcon />;
}

export function FilePathCell({ row }: { row: GroupedAlert }) {
  return (
    <FilePathCellValue
      filePath={row.filePath}
      location={row.location}
      eventKind={row.eventKind}
    />
  );
}

export function IssuesCell({ row }: { row: GroupedAlert }) {
  return <span className="text-sm">{row.occurrenceCount ?? '—'}</span>;
}

export function ScoreCell({ row }: { row: GroupedAlert }) {
  return <span className="text-sm">{row.score ?? '—'}</span>;
}

export function LastSeenCell({ row }: { row: GroupedAlert }) {
  return <RelativeTimeWithTooltip date={row.lastSeenAt} />;
}

/** KIR-5537: Last Seen on the filtered endpoint (shown while that filter is on). */
export function EndpointLastSeenCell({ row }: { row: GroupedAlert }) {
  if (!row.endpointLastSeenAt) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return <RelativeTimeWithTooltip date={row.endpointLastSeenAt} />;
}

export function FirstSeenCell({ row }: { row: GroupedAlert }) {
  return <RelativeTimeWithTooltip date={row.firstSeenAt} />;
}

export function ClientTypesCell({ row }: { row: GroupedAlert }) {
  const types = row.clientTypes ?? [];
  if (types.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  const unique = dedupeByPlatformLabel(types);
  return (
    <div className="flex flex-wrap gap-1">
      {unique.map(type => (
        <ClientTypeBadge key={type} platform={type} />
      ))}
    </div>
  );
}

export function ClientTypeCell({ value }: { value?: string | null }) {
  if (!value) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return <ClientTypeBadge platform={value} />;
}

/** Only the Multi-IDE / Platform badge explains itself; the IDEs need no tooltip. */
function ClientTypeBadge({ platform }: { platform: string }) {
  return platform === MULTI_TOOL_PLATFORM_ID ? (
    <PlatformBadge platform={platform} tooltipText={MULTI_TOOL_TOOLTIP} />
  ) : (
    <PlatformBadge platform={platform} showTooltip={false} />
  );
}

const OUTCOME_CONFIG: Record<
  string,
  {
    label: string;
    variant: 'default' | 'secondary' | 'outline';
    icon: typeof ShieldCheck;
  }
> = {
  prevented: { label: 'Prevented', variant: 'default', icon: ShieldCheck },
  detected: { label: 'Detected', variant: 'secondary', icon: Eye },
  unknown: { label: 'Unknown', variant: 'outline', icon: HelpCircle },
};

export function OutcomeBadge({
  outcome,
}: {
  outcome?: Outcome | string | null;
}) {
  const config = OUTCOME_CONFIG[outcome ?? 'unknown'] ?? OUTCOME_CONFIG.unknown;
  const Icon = config.icon;
  return (
    <Badge
      variant={config.variant}
      className={
        outcome === 'prevented'
          ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
          : outcome === 'detected'
            ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
            : ''
      }
    >
      <Icon className="size-3.5" />
      {config.label}
    </Badge>
  );
}

export function OutcomeCell({ row }: { row: GroupedAlert }) {
  if (row.aiResolved) {
    return (
      <Badge
        variant="outline"
        title="Kirin AI reviewed this match and judged it benign, for example code in a comment"
      >
        <Sparkles className="size-3.5" />
        Resolved by AI
      </Badge>
    );
  }
  return <OutcomeBadge outcome={row.outcome} />;
}

export function OccurrenceOutcomeCell({ row }: { row: AlertOccurrence }) {
  const outcome = row.outcome ?? 'unknown';
  return <OutcomeBadge outcome={outcome} />;
}
